import { z } from "zod/v4";
import { Decimal } from "decimal.js";

export const orderTypeSchema = z.string().regex(/^[0-9a-zA-Z]+$/);
export const boolCodec = z.codec(z.boolean(), z.union([z.literal(0), z.literal(1)]), {
  encode: (val) => !!val,
  decode: (val) => (val ? 1 : 0),
});

export type BoolCodecType = typeof boolCodec;

export function typeCodec<T extends string>(type: T) {
  return z.codec(z.literal(type), z.undefined(), {
    encode: () => type,
    decode: () => undefined,
  });
}

export type TypeCodecType<T extends string> = ReturnType<typeof typeCodec<T>>;

export const decimalCodec = z.codec(z.instanceof(Decimal), z.string(), {
  encode: (val) => new Decimal(val),
  decode: (val) => val.toString(),
});

export type DecimalCodecType = typeof decimalCodec;

export const NULL_UUID = "00000000-0000-0000-0000-000000000000";

const uuidNullableDomain = z.uuid().nullable();
/** Dexie may still have legacy `null`; new writes always use `NULL_UUID`. */
const uuidNullableStorage = z.uuid().nullable();

/**
 * Domain `null` (optional FK) ↔ Dexie `NULL_UUID`.
 *
 * Matches `z.codec` / `boolCodec`: the **`decode` option is the forward transform** (domain →
 * storage, i.e. `ZodType.decode`), and **`encode` is the reverse transform** (storage → domain,
 * i.e. `ZodType.encode`). Prefer `NULL_UUID` in new Dexie rows so indexed UUID columns stay
 * `z.uuid()`-parsable.
 */
export const nullableUuid = z.codec(uuidNullableDomain, uuidNullableStorage, {
  decode: (value): string => {
    if (value === null || value === NULL_UUID) return NULL_UUID;
    return value;
  },
  encode: (value): string | null => {
    if (value === null || value === NULL_UUID) return null;
    return value;
  },
});

export type NullableUuidType = typeof nullableUuid;

type NullablePartial<T> = {
  [K in keyof T]: T[K] extends z.ZodNullable
    ? T[K]
    : T[K] extends z.ZodType
      ? z.ZodNullable<T[K]>
      : never;
};

export function nullablePartial<T extends z.ZodRawShape>(
  schema: z.ZodObject<T>,
): z.ZodObject<NullablePartial<T>> {
  const shape = schema.shape;
  const newShape = Object.fromEntries(
    (Object.entries(shape) as [keyof T, z.ZodTypeAny][]).map(
      ([key, value]) => [key, value instanceof z.ZodNullable ? value : value.nullable()] as const,
    ),
  ) as NullablePartial<T>;
  return z.object(newShape);
}
